import React, { useEffect, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const COLORS = [
    "#FF6B6B", "#4ECDC4", "#45B7D1", "#FFA07A",
    "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9",
    "#F8C471", "#82E0AA", "#E74C3C", "#3498DB",
    "#2ECC71", "#9B59B6", "#F39C12", "#1ABC9C"
];

const getColorFromName = (name = "") => {
    const charCode = name.charAt(0).toUpperCase().charCodeAt(0);
    return COLORS[charCode % COLORS.length];
};

const makeRiderIcon = (heading, color) => {
    const deg = isNaN(Number(heading)) ? 0 : Number(heading);
    const uid = Math.random().toString(36).slice(2, 6);
    // Pin never rotates — only the emoji inside spins by heading.
    return L.divIcon({
        html: `
        <div style="width:42px;height:52px;">
            <svg viewBox="0 0 42 52" width="42" height="52" xmlns="http://www.w3.org/2000/svg">
                <filter id="rs-${uid}">
                    <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="${color}" flood-opacity="0.35"/>
                    <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.15"/>
                </filter>
                <path d="M21 2C12.16 2 5 9.16 5 18c0 11.5 16 31 16 31S37 29.5 37 18C37 9.16 29.84 2 21 2z"
                    fill="${color}" filter="url(#rs-${uid})"/>
                <circle cx="21" cy="17" r="11" fill="white" opacity="0.92"/>
                <circle cx="21" cy="17" r="11" fill="none" stroke="${color}" stroke-width="1.2" opacity="0.3"/>
                <!-- foreignObject fixed to pin; emoji rotates inside it by heading -->
                <foreignObject x="7" y="5" width="28" height="26">
                    <div xmlns="http://www.w3.org/1999/xhtml"
                        style="width:28px;height:26px;display:flex;align-items:center;justify-content:center;font-size:14px;line-height:1;user-select:none;transform:rotate(${deg}deg);transform-origin:center;">
                        🛵
                    </div>
                </foreignObject>
            </svg>
        </div>`,
        className: "",
        iconSize:    [42, 52],
        iconAnchor:  [21, 50],
        popupAnchor: [0, -54],
    });
};

const makeOrderIcon = () => {
    const uid = Math.random().toString(36).slice(2, 6);
    return L.divIcon({
        html: `
        <div style="width:36px;height:46px;">
            <svg viewBox="0 0 36 46" width="36" height="46" xmlns="http://www.w3.org/2000/svg">
                <filter id="oi-${uid}">
                    <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="#6366f1" flood-opacity="0.3"/>
                </filter>
                <path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 28 14 28S32 26.5 32 16C32 8.3 25.7 2 18 2z"
                    fill="#6366f1" filter="url(#oi-${uid})"/>
                <circle cx="18" cy="15" r="9.5" fill="white" opacity="0.92"/>
                <circle cx="18" cy="15" r="9.5" fill="none" stroke="#6366f1" stroke-width="1" opacity="0.25"/>
                <foreignObject x="5" y="4" width="26" height="24">
                    <div xmlns="http://www.w3.org/1999/xhtml"
                        style="width:26px;height:24px;display:flex;align-items:center;justify-content:center;font-size:13px;line-height:1;user-select:none;">
                        📦
                    </div>
                </foreignObject>
            </svg>
        </div>`,
        className: "",
        iconSize:    [36, 46],
        iconAnchor:  [18, 44],
        popupAnchor: [0, -46],
    });
};

function FlyTo({ target }) {
    const map = useMap();
    useEffect(() => {
        if (target) map.flyTo([target.lat, target.lng], 17, { duration: 1.5 });
    }, [target, map]);
    return null;
}

function FitBounds({ points }) {
    const map = useMap();
    useEffect(() => {
        if (points.length > 1) {
            map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 15 });
        } else if (points.length === 1) {
            map.setView(points[0], 15);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}

const statusMap = {
    1: { label: "Pending",    cls: "order-status--pending"   },
    2: { label: "Accepted",   cls: "order-status--assigned"  },
    3: { label: "Preparing",  cls: "order-status--pickedup"  },
    4: { label: "On the Way", cls: "order-status--assigned"  },
    5: { label: "Delivered",  cls: "order-status--completed" },
    6: { label: "Cancelled",  cls: "order-status--cancelled" },
};

const StatusBadge = ({ status }) => {
    const cfg = statusMap[status] || { label: `Status ${status}`, cls: "order-status--pending" };
    return (
        <div className={`order-status ${cfg.cls}`} style={{ marginBottom: 0, padding: "5px 10px" }}>
            <span className="order-status__label" style={{ fontSize: 11 }}>
                <span className="order-status__dot" />
                {cfg.label}
            </span>
        </div>
    );
};

const RiderPopupContent = ({ point, color }) => {
    const detail = point?.detail || {};
    const name   = point?.name || detail?.name || "Rider";
    return (
        <div style={{ fontFamily: "'Inter', sans-serif", padding: "10px 14px", minWidth: 160 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, flexShrink: 0,
                }}>🛵</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{name}</div>
            </div>
            <div style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "#eef2ff", borderRadius: 8, padding: "6px 10px",
            }}>
                <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 600 }}>Pending orders</span>
                <span style={{
                    marginLeft: "auto",
                    background: "#6366f1", color: "#fff",
                    borderRadius: 20, padding: "1px 8px",
                    fontSize: 12, fontWeight: 800,
                    fontFamily: "'DM Mono', monospace",
                }}>{point?.non_complete_orders ?? 0}</span>
            </div>
        </div>
    );
};

const OrderPopupContent = ({ order }) => (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
        {/* Header */}
        <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "11px 14px 10px",
            borderBottom: "3px solid #6366f1",
            background: "linear-gradient(135deg, #eef2ff 0%, #ffffff 100%)",
        }}>
            <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "#6366f1",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0,
                boxShadow: "0 2px 8px #6366f155",
            }}>📦</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                    {order.name || "Customer"}
                </div>
                <div style={{ fontSize: 10, color: "#6366f1", fontFamily: "'DM Mono', monospace", marginTop: 2 }}>
                    #{order.id}
                </div>
            </div>
        </div>

        {/* Info rows */}
        <div style={{ padding: "8px 14px 4px" }}>
            {order.restaurant && (
                <div className="tm-popup-row">
                    <span className="tm-popup-row-icon">🍽️</span>
                    <span className="tm-popup-row-label">From</span>
                    <span className="tm-popup-row-value" style={{ fontWeight: 600 }}>{order.restaurant}</span>
                </div>
            )}
            {order.address && (
                <div className="tm-popup-row">
                    <span className="tm-popup-row-icon">📍</span>
                    <span className="tm-popup-row-label">To</span>
                    <span className="tm-popup-row-value" style={{ fontSize: 11, lineHeight: 1.4 }}>{order.address}</span>
                </div>
            )}
        </div>

        {/* Status */}
        <div style={{ padding: "0 14px 10px" }}>
            <StatusBadge status={order.status} />
        </div>
    </div>
);

function HoverMarker({ position, icon, children }) {
    const markerRef = useRef(null);
    return (
        <Marker
            position={position}
            icon={icon}
            ref={markerRef}
            eventHandlers={{
                mouseover: () => markerRef.current?.openPopup(),
                mouseout:  () => markerRef.current?.closePopup(),
                click:     () => markerRef.current?.openPopup(),
            }}
        >
            <Popup autoPan={false} keepInView={false} closeButton={false}>
                {children}
            </Popup>
        </Marker>
    );
}

const TrackingMap = ({ points = [], orderPoints = [] }) => {
    const [flyTarget, setFlyTarget] = useState(null);

    const validPoints = points.filter(
        (p) => p?.detail?.delivery_lat != null && p?.detail?.delivery_long != null
    );

    const formattedOrders = orderPoints
        .map((o) => {
            try {
                const loc = typeof o.location === "string" ? JSON.parse(o.location) : o.location;
                return {
                    id:         o.order_id,
                    name:       o.customer_name,
                    lat:        parseFloat(loc.lat),
                    lng:        parseFloat(loc.lng),
                    address:    loc.address,
                    status:     o.orderstatus_id,
                    restaurant: o.restaurant_name,
                };
            } catch { return null; }
        })
        .filter((o) => o && o.lat && o.lng);

    const allBoundsPoints = [
        ...validPoints.map((p) => [p.detail.delivery_lat, p.detail.delivery_long]),
        ...formattedOrders.map((o) => [o.lat, o.lng]),
    ];

    const handleLocate = useCallback((lng, lat) => {
        setFlyTarget({ lat, lng, _t: Date.now() });
    }, []);

    return (
        <>
            {/* Your exact wrapper structure preserved */}
            <div className="map-dashboard-wrapper">
                <div className="map-container-main">
                    <MapContainer
                        center={[16.4346, 80.5662]}
                        zoom={15}
                        style={{ width: "100%", height: "100%" }}
                        attributionControl={false}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                        />

                        {allBoundsPoints.length > 0 && <FitBounds points={allBoundsPoints} />}
                        {flyTarget && <FlyTo target={flyTarget} key={flyTarget._t} />}

                        {validPoints.map((p, idx) => {
                            const color = getColorFromName(p?.name);
                            return (
                                <HoverMarker
                                    key={`rider-${idx}`}
                                    position={[p.detail.delivery_lat, p.detail.delivery_long]}
                                    icon={makeRiderIcon(p.detail.heading, color)}
                                >
                                    <RiderPopupContent point={p} color={color} />
                                </HoverMarker>
                            );
                        })}

                        {formattedOrders.map((o, idx) => (
                            <HoverMarker
                                key={`order-${idx}`}
                                position={[o.lat, o.lng]}
                                icon={makeOrderIcon()}
                            >
                                <OrderPopupContent order={o} />
                            </HoverMarker>
                        ))}
                    </MapContainer>
                </div>

                {/* Sidebar — your exact classes and structure preserved */}
                <div className="map-sidebar">
                    <div className="section-header">
                        <p className="eagleview-title">Eagle View</p>
                        <span className="eagleview-count">{validPoints?.length}</span>
                    </div>

                    <div className="indicator-list">
                        {validPoints.map((p, idx) => (
                            <div
                                style={{borderColor: getColorFromName(p?.name), borderWidth: 1}}
                                key={`list-${idx}`}
                                className="indicator-item"
                                onClick={() => handleLocate(p?.detail?.delivery_long, p?.detail?.delivery_lat)}
                            >
                                {p?.detail?.photo ? (
                                    <div className="dp-avatar">
                                        <img src={"https://food.totalfresh.co/assets/img/delivery/" + p?.detail?.photo} className="dp-avatar" alt={"nothing"}/>
                                    </div>
                                 ): (
                                     <span
                                         className="dot"
                                         style={{backgroundColor: getColorFromName(p?.name)}}
                                     >
                                         {(p?.detail?.name || p?.name || "?").charAt(0)}
                                     </span>
                                )}
                                <div className="item-info">
                                    <div className="item-name">{p?.name}</div>
                                    <div>Pending orders: {p?.non_complete_orders}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default TrackingMap;